import { z } from 'zod';
import { CourseKindSchema } from '../academy/course';
// ============================================
// Billing — Fatura dönemi + öğrenci faturası + dekont
// ============================================
//
// PDF ödeme feedback (2026-09):
//  - Dönem oluştur = GROUP + billingActive öğrenci snapshot (BillingPeriodLine)
//  - Geçmiş dönemler dondurulur; ücret değişikliği yalnız aktif dönemi günceller
//  - Durum: unpaid → awaiting_approval (veli dekont) → paid (admin onay)
//  - Dönem Tutarı (flat amount) kaldırıldı — ücretler ders satırlarından gelir
// ---- Billing Period ----
export const BillingPeriodSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    startsAt: z.string().datetime(),
    endsAt: z.string().datetime(),
    createdById: z.string().uuid().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    // hydrated (server fills)
    invoiceCount: z.number().int().nonnegative().optional(),
    paidCount: z.number().int().nonnegative().optional(),
    totalExpected: z.number().nonnegative().optional(),
    totalPaid: z.number().nonnegative().optional(),
    isActive: z.boolean().optional(),
});
export const CreateBillingPeriodSchema = z
    .object({
    name: z.string().min(1).max(200),
    startsAt: z.string().datetime(),
    endsAt: z.string().datetime(),
})
    .refine((v) => new Date(v.endsAt).getTime() >= new Date(v.startsAt).getTime(), {
    message: 'endsAt must be on or after startsAt',
    path: ['endsAt'],
});
export const CreateBillingPeriodResultSchema = z.object({
    period: BillingPeriodSchema,
    generated: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
});
// ---- Period line snapshot ----
export const BillingPeriodLineSchema = z.object({
    id: z.string().uuid(),
    billingPeriodId: z.string().uuid(),
    studentUserId: z.string().uuid(),
    courseId: z.string().uuid(),
    courseName: z.string(),
    fee: z.number().nonnegative(),
    paid: z.boolean(),
    paidAt: z.string().datetime().nullable(),
});
// ---- Student Invoice ----
export const StudentInvoiceStatusSchema = z.enum([
    'unpaid',
    'partial',
    'paid',
    'waived',
    'awaiting_approval',
]);
export const StudentInvoiceSchema = z.object({
    id: z.string().uuid(),
    billingPeriodId: z.string().uuid().nullable(),
    courseId: z.string().uuid().nullable(),
    studentUserId: z.string().uuid(),
    expectedAmount: z.number().nonnegative(),
    paidAmount: z.number().nonnegative(),
    paidAt: z.string().datetime().nullable(),
    status: StudentInvoiceStatusSchema,
    note: z.string().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    studentName: z.string().nullable().optional(),
    parentName: z.string().nullable().optional(),
    parentPhone: z.string().nullable().optional(),
    periodName: z.string().nullable().optional(),
    courseName: z.string().nullable().optional(),
    courseKind: CourseKindSchema.nullable().optional(),
});
export const CreateStudentInvoiceSchema = z.object({
    studentUserId: z.string().uuid(),
    billingPeriodId: z.string().uuid(),
    expectedAmount: z.coerce.number().nonnegative().optional(),
});
export const UpdateStudentInvoiceSchema = z.object({
    expectedAmount: z.coerce.number().nonnegative().optional(),
    paidAmount: z.coerce.number().nonnegative().optional(),
    paidAt: z.string().datetime().nullable().optional(),
    status: StudentInvoiceStatusSchema.optional(),
    note: z.string().max(1000).nullable().optional(),
});
/** Dönem detayında ders bazlı ödeme kaydı (checkbox + kaydet). */
export const SavePeriodPaymentSchema = z.object({
    lines: z
        .array(z.object({
        courseId: z.string().uuid(),
        paid: z.boolean(),
    }))
        .min(1)
        .max(50),
});
// ---- Expected amount (detay sayfasi) ----
export const StudentExpectedAmountSchema = z.object({
    studentUserId: z.string().uuid(),
    override: z.number().nonnegative().nullable(),
    computed: z.number().nonnegative(),
    effective: z.number().nonnegative(),
    groupTotal: z.number().nonnegative(),
    campTotal: z.number().nonnegative(),
    privateTotal: z.number().nonnegative(),
    courses: z.array(z.object({
        courseId: z.string().uuid(),
        name: z.string(),
        kind: CourseKindSchema,
        courseFee: z.number().nonnegative().nullable(),
        studentFee: z.number().nonnegative().nullable(),
        enrollmentFee: z.number().nonnegative().nullable(),
    })),
});
export const SetStudentFeeOverrideSchema = z.object({
    monthlyFeeOverride: z.coerce.number().nonnegative().nullable(),
});
export const SetEnrollmentFeeSchema = z.object({
    courseId: z.string().uuid(),
    studentFee: z.coerce.number().nonnegative().nullable(),
});
export const SetBillingActiveSchema = z.object({
    billingActive: z.boolean(),
});
// ---- Student payment summary (Sayfa 1 / dönem tablosu) ----
/** Dönem snapshot'ındaki bir dersin indirimli ücreti. */
export const StudentPaymentCourseFeeSchema = z.object({
    courseId: z.string().uuid(),
    courseName: z.string(),
    fee: z.number().nonnegative(),
});
export const StudentPaymentSummarySchema = z.object({
    studentUserId: z.string().uuid(),
    studentName: z.string().nullable(),
    parentName: z.string().nullable(),
    parentPhone: z.string().nullable(),
    hasUnpaidInvoice: z.boolean(),
    unpaidCount: z.number().int().nonnegative(),
    outstandingAmount: z.number().nonnegative(),
    /** unpaid | awaiting_approval | paid — dönem kapsamındaki görünür durum */
    paymentStatus: StudentInvoiceStatusSchema.optional(),
    invoiceId: z.string().uuid().nullable().optional(),
    billingPeriodId: z.string().uuid().nullable().optional(),
    periodName: z.string().nullable().optional(),
    coursesSummary: z.string().nullable().optional(),
    /** Alınan eğitimlerin dönem snapshot indirimli ücretleri (grup sekmesi). */
    courseFees: z.array(StudentPaymentCourseFeeSchema).optional(),
    /** Dönem faturasının beklenen toplamı. */
    totalDue: z.number().nonnegative().optional(),
    /** Dönem faturasında ödenen tutar. */
    totalPaid: z.number().nonnegative().optional(),
    groupOutstanding: z.number().nonnegative().optional(),
    campOutstanding: z.number().nonnegative().optional(),
    privateOutstanding: z.number().nonnegative().optional(),
});
export const StudentPaymentQuerySchema = z.object({
    courseId: z.string().uuid().optional(),
    gradeLevel: z.coerce.number().int().min(1).max(12).optional(),
    classSection: z.string().max(20).optional(),
    kind: CourseKindSchema.optional(),
    /** Grup sekmesinde seçili fatura dönemi — yoksa aktif dönem. */
    billingPeriodId: z.string().uuid().optional(),
    courseIds: z
        .preprocess((v) => (typeof v === 'string' ? v.split(',').filter(Boolean) : v), z.array(z.string().uuid()).max(100))
        .optional(),
});
// ---- Payment Receipt ----
export const PaymentReceiptStatusSchema = z.enum(['pending', 'approved', 'rejected']);
export const PaymentReceiptSchema = z.object({
    id: z.string().uuid(),
    studentInvoiceId: z.string().uuid(),
    uploadedByUserId: z.string().uuid(),
    fileUrl: z.string(),
    declaredAmount: z.number().nonnegative().nullable(),
    note: z.string().nullable(),
    status: PaymentReceiptStatusSchema,
    reviewedById: z.string().uuid().nullable(),
    reviewedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    uploaderName: z.string().nullable().optional(),
    studentName: z.string().nullable().optional(),
    periodName: z.string().nullable().optional(),
});
export const CreatePaymentReceiptSchema = z
    .object({
    studentInvoiceId: z.string().uuid(),
    fileUrl: z.string().min(1).max(8_000_000),
    declaredAmount: z.coerce.number().nonnegative().optional(),
    note: z.string().max(1000).optional(),
})
    .refine((v) => /^data:|^https?:\/\//i.test(v.fileUrl), {
    message: 'fileUrl must be a data: or http(s) URL',
    path: ['fileUrl'],
});
export const ReviewPaymentReceiptSchema = z.object({
    status: z.enum(['approved', 'rejected']),
});
// ---- Student payment detail ----
export const StudentPeriodPaymentSchema = z.object({
    period: BillingPeriodSchema,
    invoice: StudentInvoiceSchema.nullable(),
    lines: z.array(BillingPeriodLineSchema),
    totalDue: z.number().nonnegative(),
    totalPaid: z.number().nonnegative(),
});
export const StudentPaymentDetailSchema = z.object({
    student: z.object({
        userId: z.string().uuid(),
        name: z.string().nullable(),
        email: z.string().nullable(),
        phone: z.string().nullable(),
        billingActive: z.boolean().optional(),
    }),
    parent: z
        .object({
        name: z.string().nullable(),
        phone: z.string().nullable(),
        relationship: z.string().nullable(),
    })
        .nullable(),
    expected: StudentExpectedAmountSchema,
    /** Dönem snapshot satırları (öğrenci profil Ödemeler grid + detay). */
    periodPayments: z.array(StudentPeriodPaymentSchema).optional(),
    invoices: z.array(StudentInvoiceSchema),
    receipts: z.array(PaymentReceiptSchema),
});
//# sourceMappingURL=billing.js.map